import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useGameStore } from '../../../store/gameStore';
import { useTimer } from '../../../hooks/useTimer';
import { useTelegram } from '../../../hooks/useTelegram';
import SudokuGrid from './SudokuGrid';
import NumberPad from './NumberPad';
import GameControls from './GameControls';
import GameHeader from './GameHeader';
import Toast from '../../../components/Toast';
import type { Difficulty } from '../../../../../shared/types';

export default function GameBoard() {
  const { t } = useTranslation();
  const { showConfirm, notificationHaptic, haptic } = useTelegram();
  const currentGame = useGameStore(state => state.currentGame);
  const isPaused = useGameStore(state => state.isPaused);
  const errors = useGameStore(state => state.errors);
  const startNewGame = useGameStore(state => state.startNewGame);
  const [lastErrorCount, setLastErrorCount] = useState(0);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [hasShownVictoryToast, setHasShownVictoryToast] = useState(false);
  const [hasShownDefeatToast, setHasShownDefeatToast] = useState(false);
  const [showDefeatOverlay, setShowDefeatOverlay] = useState(false);
  const timer = useGameStore(state => state.timer);
  
  useTimer(); // Start timer

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    // Reset toast flags when new game starts
    if (currentGame?.status === 'in_progress') {
      setHasShownVictoryToast(false);
      setHasShownDefeatToast(false);
      setShowDefeatOverlay(false);
    }
  }, [currentGame?.id]);

  useEffect(() => {
    if (currentGame?.status === 'completed' && !hasShownVictoryToast) {
      notificationHaptic('success');
      // TODO: Get actual rank from backend
      const rank = Math.floor(Math.random() * 1000) + 1; // Заглушка
      const timeStr = formatTime(currentGame.timeElapsed || timer);
      setToastMessage(`${t('game.messages.victory', { rank })} Время: ${timeStr}`);
      setHasShownVictoryToast(true);
    } else if (currentGame?.status === 'abandoned' && errors >= 3 && !hasShownDefeatToast && currentGame?.id) {
      // Only show defeat toast if game actually existed (not on new game start)
      notificationHaptic('error');
      setToastMessage(`${t('game.status.failed')} ${t('game.messages.tooManyErrors')}`);
      setHasShownDefeatToast(true);
    }
  }, [currentGame?.status, currentGame?.id, currentGame?.timeElapsed, errors, hasShownVictoryToast, hasShownDefeatToast, notificationHaptic, t, timer]);

  // Show toast when error count increases
  useEffect(() => {
    if (errors > lastErrorCount && errors < 3) {
      notificationHaptic('error');
      setToastMessage(t('game.messages.wrongNumber', { count: errors, max: 3 }));
      setLastErrorCount(errors);
    } else if (errors >= 3 && lastErrorCount < 3) {
      // Show defeat toast and overlay when reaching 3 errors
      notificationHaptic('error');
      setToastMessage(`${t('game.status.failed')} ${t('game.messages.tooManyErrors')}`);
      setHasShownDefeatToast(true);
      setLastErrorCount(errors);
      // Show overlay immediately (blur the board)
      setShowDefeatOverlay(true);
    } else if (errors < lastErrorCount) {
      setLastErrorCount(errors);
    }
  }, [errors, lastErrorCount, notificationHaptic, t]);

  const handleDifficultyChange = async (difficulty: Difficulty) => {
    if (currentGame && currentGame.status === 'in_progress') {
      const confirmed = await showConfirm(t('game.messages.confirmRestart'));
      if (!confirmed) return;
    }
    haptic('medium');
    startNewGame(difficulty);
  };

  // Stable callback for toast close
  const handleToastClose = useCallback(() => {
    setToastMessage(null);
  }, []);

  return (
    <div className="flex flex-col h-screen">
      {/* Toast notification */}
      {toastMessage && (
        <Toast
          key={toastMessage} // Force remount when message changes
          message={toastMessage}
          type={currentGame?.status === 'completed' ? 'success' : 'error'}
          duration={currentGame?.status === 'completed' ? 5000 : 2000}
          onClose={handleToastClose}
        />
      )}
      
      {/* Header with difficulty and timer */}
      <GameHeader onDifficultyChange={handleDifficultyChange} />

      {/* Main content */}
      <div className="flex-1 flex flex-col gap-3 p-4 overflow-y-auto">
        {/* Sudoku Grid */}
        <div className="relative">
          <SudokuGrid />
          {isPaused && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-20 flex items-center justify-center rounded-lg pointer-events-auto">
              <div className="text-center">
                <p className="text-2xl font-bold mb-2">{t('game.status.paused')}</p>
                <p className="text-muted-foreground">{t('game.resume')}</p>
              </div>
            </div>
          )}
          {showDefeatOverlay && errors >= 3 && currentGame && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-20 flex flex-col items-center justify-center rounded-lg gap-4 pointer-events-auto">
              <div className="text-center">
                <p className="text-2xl font-bold mb-2 text-destructive">{t('game.status.failed')}</p>
                <p className="text-muted-foreground mb-4">{t('game.messages.tooManyErrors')}</p>
                <button
                  onClick={() => handleDifficultyChange(currentGame.difficulty)}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                >
                  {t('game.newGame')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Number Pad - moved up, under the grid */}
        <NumberPad />

        {/* Game Controls */}
        <GameControls />

      </div>

    </div>
  );
}

